import { termMaxApiBaseUrl } from '@1delta/data-sdk'
import type {
  TermMaxBookTop,
  TermMaxCurveSegment,
  TermMaxDataSource,
  TermMaxFeeConfig,
  TermMaxMarketConfig,
  TermMaxMarketRaw,
  TermMaxOrderState,
} from './types'

/** Hosted TermMax data API (Swagger at `/api-docs`, spec at `/api-docs-json`). */
export const DEFAULT_TERMMAX_API = 'https://api.termmax.ts.finance'

const LENDING_DEBUG = false
const warn = (...args: unknown[]) => {
  if (LENDING_DEBUG) console.warn('[termmax-api]', ...args)
}

type FetchLike = typeof fetch

/** Resolve a chain's TermMax API base (config override → hosted default). */
export function termMaxApiBase(chainId: string): string {
  return termMaxApiBaseUrl(chainId) || DEFAULT_TERMMAX_API
}

const toBig = (v: unknown): bigint => {
  try {
    if (v == null) return 0n
    if (typeof v === 'bigint') return v
    if (typeof v === 'number') return BigInt(Math.trunc(v))
    const s = String(v).trim()
    if (s === '') return 0n
    // Upstream sometimes reports decimal-formatted amounts; truncate.
    return BigInt(s.includes('.') ? s.slice(0, s.indexOf('.')) : s)
  } catch {
    return 0n
  }
}

const toNum = (v: unknown): number => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

const lower = (v: unknown): string => String(v ?? '').toLowerCase()

function parseCuts(raw: any): TermMaxCurveSegment[] {
  if (!Array.isArray(raw)) return []
  return raw.map((c: any) => ({
    xtReserve: toBig(c?.xtReserve),
    liqSquare: toBig(c?.liqSquare),
    offset: toBig(c?.offset),
  }))
}

function parseFeeConfig(raw: any): TermMaxFeeConfig | undefined {
  if (!raw) return undefined
  return {
    lendTakerFeeRatio: toBig(raw.lendTakerFeeRatio),
    lendMakerFeeRatio: toBig(raw.lendMakerFeeRatio),
    borrowTakerFeeRatio: toBig(raw.borrowTakerFeeRatio),
    borrowMakerFeeRatio: toBig(raw.borrowMakerFeeRatio),
    mintGtFeeRatio: toBig(raw.mintGtFeeRatio),
    mintGtFeeRef: toBig(raw.mintGtFeeRef),
  }
}

/**
 * TermMax "Alpha" (Spec Trading) series detector.
 *
 * TermMax reuses the exact same market machinery for an OPTIONS product: the
 * FT is a strike-series payoff token, named `FT:QQQ/USDG@21AUG2026-630P`
 * ($630 put) / `...-770C` (call). The market CONFIG is indistinguishable from
 * a lending market — same shape, and `version: 'v2_01'` also serves ordinary
 * lending markets on Ethereum/Arbitrum, so the version is NOT a filter. Nor is
 * `liquidatable: false`, which legitimate RWA lending markets also carry. The
 * ONLY reliable marker is the strike suffix on the FT token's own symbol,
 * which follows the maturity date stamp.
 *
 * These must never reach the lending surface: the API's per-order `apr` for a
 * series is an annualization of the OPTION PREMIUM, not a lend rate — observed
 * as a flat 220% on every put series and ~100% on every call series on
 * Robinhood chain (4663) and BNB, with real (small) posted capacity, so the
 * zero-capacity guard in {@link reduceTop} does not catch them and they sort
 * straight to the top of every APR ranking.
 */
export function isTermMaxSpecSeriesSymbol(symbol: unknown): boolean {
  return /@\d{1,2}[A-Z]{3}\d{4}-\d+(\.\d+)?[CP]$/.test(String(symbol ?? ''))
}

/** Unix seconds from an ISO timestamp; 0 when unparseable. */
function toUnix(iso: unknown): number {
  if (typeof iso === 'number') return Math.trunc(iso)
  const t = Date.parse(String(iso ?? ''))
  return Number.isFinite(t) ? Math.floor(t / 1000) : 0
}

/**
 * Project one upstream market config onto {@link TermMaxMarketConfig}.
 * Returns null when the row lacks the addresses we cannot work without.
 */
function parseMarket(
  raw: any,
  assetDecimals: Map<string, number>,
): TermMaxMarketConfig | null {
  const c = raw?.contracts
  if (!c?.marketAddr || !c?.underlyingAddr || !c?.collateralAddr) return null
  const debtToken = lower(c.underlyingAddr)
  const collateral = lower(c.collateralAddr)
  return {
    market: lower(c.marketAddr),
    routerAddr: c.routerAddr ? lower(c.routerAddr) : undefined,
    ft: lower(c.ftAddr),
    xt: lower(c.xtAddr),
    gt: lower(c.gtAddr),
    debtToken,
    // A missing entry means the payload changed shape — fall back to 18 so the
    // row still renders, but flag it so it is visible rather than silent.
    debtDecimals: assetDecimals.get(debtToken) ?? 18,
    debtDecimalsResolved: assetDecimals.has(debtToken),
    collateral,
    collateralDecimals: assetDecimals.get(collateral) ?? 18,
    collateralDecimalsResolved: assetDecimals.has(collateral),
    symbol: raw.symbol,
    maturity: toUnix(raw.maturity),
    maxLtv: String(raw.maxLtv ?? '0'),
    liquidationLtv: String(raw.liquidationLtv ?? '0'),
    liquidatable: raw.liquidatable !== false,
    liquidationWindowSeconds: toNum(raw.liquidationWindowSeconds) || undefined,
    version: raw.version,
    feeConfig: parseFeeConfig(raw.defaultFeeConfig),
    isMatured: raw.isMatured === true,
    isEnabled: raw.isEnabled !== false,
  }
}

/**
 * Project one upstream order onto {@link TermMaxOrderState}, applying the
 * MAKER→TAKER crossing exactly once:
 *
 *   upstream `lendCurve`  (maker lends)   → `takerBorrowCuts`
 *   upstream `borrowCurve`(maker borrows) → `takerLendCuts`
 *   upstream `lcft.apr`                   → taker BORROW apr
 *   upstream `bcft.apr`                   → taker LEND apr
 *
 * This is the ONLY place the crossing is applied. See types.ts.
 */
function parseOrder(raw: any): TermMaxOrderState | null {
  const oc = raw?.orderConfig?.contracts ?? raw?.orderInfo
  const orderAddress = lower(oc?.orderAddr ?? raw?.orderInfo?.orderAddress)
  const marketAddress = lower(oc?.marketAddr ?? raw?.orderInfo?.marketAddress)
  if (!orderAddress || !marketAddress) return null

  const state = raw?.orderInfo?.contractState ?? raw?.contractState ?? {}
  const meta = raw?.metadata ?? {}
  const term = raw?.priceInfo?.term ?? {}

  return {
    orderAddress,
    marketAddress,
    makerAddress: raw?.orderConfig?.makerAddress
      ? lower(raw.orderConfig.makerAddress)
      : undefined,
    makerIsVault: state.makerIsVault === true,
    virtualXtReserve: toBig(state.virtualXtReserve),
    ftReserve: toBig(state.ftReserve),
    xtReserve: toBig(state.xtReserve),
    maxXtReserve: toBig(state.maxXtReserve),
    gtId: toBig(state.gtId),
    // ── the crossing ──
    takerLendCuts: parseCuts(state.borrowCurve),
    takerBorrowCuts: parseCuts(state.lendCurve),
    takerLendApr: term?.bcft?.apr != null ? toNum(term.bcft.apr) : undefined,
    takerBorrowApr: term?.lcft?.apr != null ? toNum(term.lcft.apr) : undefined,
    feeConfig: parseFeeConfig(state.feeConfig),
    lendCapacityUsd: toNum(meta.lendCapacityValue),
    borrowCapacityUsd: toNum(meta.borrowCapacityValue),
    lendCapacityAmount: toNum(meta.lendCapacityAmount),
    borrowCapacityAmount: toNum(meta.borrowCapacityAmount),
  }
}

/**
 * Minimum executable capacity (USD) an order must post on a side before its
 * rate can become the market's advertised best.
 *
 * A `> 0` guard alone is not enough: live orders post DUST — RLUSD/USPC on
 * Ethereum quoted a 3.46% borrow against $0.14 of capacity (2026-08-18), a
 * rate no real size can execute at, while TermMax's own UI showed the side as
 * empty ("Place order"). Dust still counts toward the aggregate depth figures;
 * it just cannot headline. $10 splits the observed field cleanly (their UI
 * advertises at ~$48 and hides at ~$0.14).
 */
export const TERMMAX_MIN_ADVERTISED_CAPACITY_USD = 10

/**
 * Collapse every order in a market to the best rate per side + aggregate depth.
 *
 * Order-agnostic (we scan all orders, we do not trust upstream ordering):
 * best LEND = the HIGHEST taker-lend APR, best BORROW = the LOWEST taker-borrow
 * APR. Orders that quote a side with less than
 * {@link TERMMAX_MIN_ADVERTISED_CAPACITY_USD} of capacity are ignored for
 * "best" so a dead or dust order cannot advertise an unreachable rate, but
 * they still contribute their (small) depth.
 *
 * NOTE the AGGREGATE may legitimately cross (best lend > best borrow) when two
 * different makers quote the two sides — that is a real, if fee-eaten, carry
 * trade, not a bug. Do NOT "fix" it by clamping. The invariant that must hold
 * is per-ORDER (a single maker's two-sided quote always keeps a spread), and
 * that is what the crossing in `parseOrder` guarantees; it was verified against
 * 35 live two-sided orders across 4 chains with zero violations.
 */
export function reduceTop(orders: TermMaxOrderState[]): TermMaxBookTop {
  let bestLendApr: number | undefined
  let bestBorrowApr: number | undefined
  let lendDepthUsd = 0
  let borrowDepthUsd = 0
  let lendDepthAmount = 0
  let borrowDepthAmount = 0

  for (const o of orders) {
    lendDepthUsd += o.lendCapacityUsd
    borrowDepthUsd += o.borrowCapacityUsd
    lendDepthAmount += o.lendCapacityAmount
    borrowDepthAmount += o.borrowCapacityAmount

    if (
      o.takerLendApr != null &&
      o.takerLendApr > 0 &&
      o.lendCapacityUsd >= TERMMAX_MIN_ADVERTISED_CAPACITY_USD
    ) {
      if (bestLendApr === undefined || o.takerLendApr > bestLendApr) {
        bestLendApr = o.takerLendApr
      }
    }
    if (
      o.takerBorrowApr != null &&
      o.takerBorrowApr > 0 &&
      o.borrowCapacityUsd >= TERMMAX_MIN_ADVERTISED_CAPACITY_USD
    ) {
      if (bestBorrowApr === undefined || o.takerBorrowApr < bestBorrowApr) {
        bestBorrowApr = o.takerBorrowApr
      }
    }
  }

  return {
    bestLendApr,
    bestBorrowApr,
    lendDepthUsd,
    borrowDepthUsd,
    lendDepthAmount,
    borrowDepthAmount,
  }
}

/**
 * Hosted-API data source. ONE call per chain — `GET /market/data?chainId=`
 * returns global config, asset configs, markets, order configs and live order
 * state together (~240KB on Ethereum).
 *
 * This is the swappable seam: a self-hosted indexer or a pure on-chain reader
 * can implement {@link TermMaxDataSource} later without touching callers.
 *
 * Reliability posture: the endpoint is an undocumented app backend (no
 * versioning or rate-limit statement, `/admin/*` routes on the same host), so
 * treat it exactly like the Morpho GraphQL API — primary, but the caller keeps
 * a last-known-good snapshot (see fetchPublic.ts).
 */
export class TermMaxApiSource implements TermMaxDataSource {
  private readonly baseUrl: string
  private readonly fetchImpl: FetchLike

  constructor(baseUrl: string, fetchImpl: FetchLike = fetch) {
    this.baseUrl = baseUrl.replace(/\/+$/, '')
    // Call fetch through globalThis: storing the global `fetch` on the instance
    // and invoking `this.fetchImpl(...)` sets `this` to the instance, which the
    // Cloudflare Workers runtime rejects with "Illegal invocation" (Node/undici
    // tolerates it, so this only breaks in the deployed worker). A
    // caller-supplied stub is unaffected by the extra `this` arg.
    this.fetchImpl = (input: any, init?: any) =>
      fetchImpl.call(globalThis, input, init)
  }

  async getChainMarkets(chainId: string): Promise<TermMaxMarketRaw[] | null> {
    const url = `${this.baseUrl}/market/data?chainId=${chainId}`
    let json: any
    try {
      const res = await this.fetchImpl(url)
      if (!res.ok) {
        warn(`market/data ${chainId} → HTTP ${res.status}`)
        return null
      }
      json = await res.json()
    } catch (e) {
      warn(`market/data ${chainId} fetch failed:`, (e as any)?.message ?? e)
      return null
    }

    const data = json?.data ?? json
    if (!data) return null

    // Asset decimals come from the same payload, so no extra round-trip.
    //
    // FIELD NAMES MATTER: `assetConfigs` rows use `contractAddress` / `decimals`
    // — NOT `assetAddress` / `assetDecimals`, which is what the per-order
    // `priceInfo.base.*` blocks use for the same concepts. Reading the wrong
    // pair silently yields an EMPTY map, every market falls through to the
    // 18-decimal default, and every 6-dec stablecoin market is then off by
    // 10^12. Both spellings are accepted here so a shape change on either side
    // cannot resurrect that.
    const assetDecimals = new Map<string, number>()
    const assetSymbols = new Map<string, string>()
    for (const a of Array.isArray(data.assetConfigs) ? data.assetConfigs : []) {
      const addr = lower(a?.contractAddress ?? a?.assetAddress)
      if (!addr) continue
      const dec = Number(a?.decimals ?? a?.assetDecimals)
      if (Number.isFinite(dec)) assetDecimals.set(addr, dec)
      if (a?.symbol) assetSymbols.set(addr, String(a.symbol))
    }

    const markets: TermMaxMarketConfig[] = []
    for (const m of Array.isArray(data.markets) ? data.markets : []) {
      const parsed = parseMarket(m, assetDecimals)
      // Matured markets normally vanish from upstream entirely; the flags are
      // belt-and-braces for the window where they have not yet.
      if (!parsed || !parsed.isEnabled || parsed.isMatured) continue
      // Spec/Alpha option series are NOT lending markets — see
      // isTermMaxSpecSeriesSymbol. The strike lives only on the FT token's
      // symbol (the market's own symbol is the bare pair), so this needs the
      // assetConfigs join. A missing FT row fails OPEN: better a mispriced row
      // a human can see than silently dropping real markets on a payload-shape
      // change.
      if (isTermMaxSpecSeriesSymbol(assetSymbols.get(parsed.ft))) continue
      markets.push(parsed)
    }
    if (markets.length === 0) return []

    const ordersByMarket = new Map<string, TermMaxOrderState[]>()
    for (const o of Array.isArray(data.orders) ? data.orders : []) {
      const parsed = parseOrder(o)
      if (!parsed) continue
      const list = ordersByMarket.get(parsed.marketAddress)
      if (list) list.push(parsed)
      else ordersByMarket.set(parsed.marketAddress, [parsed])
    }

    return markets.map((config) => {
      const orders = ordersByMarket.get(config.market) ?? []
      return { config, top: reduceTop(orders), orders }
    })
  }
}

/** Default data source for a chain (hosted API, resolved per chain). */
export function createTermMaxDataSource(
  chainId: string,
  fetchImpl: FetchLike = fetch,
): TermMaxDataSource {
  return new TermMaxApiSource(termMaxApiBase(chainId), fetchImpl)
}
